import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import { useCategoriasSchema, useDiferencaCategoriasSchema } from 'app/main/compra/components/modal/compraSchema';
import * as yup from 'yup';

const gtZero = (value) => value !== '' && value !== null && value !== undefined && value > 0;

const produtosServicosVendaPadraoSchema = yup.array(
  yup.object().shape({
    produtoServico: yup
      .object()
      .nullable()
      .required(mensagemCampoObrigatorio('o produto/serviço')),
    quantidade: yup
      .number()
      .nullable()
      .required(mensagemCampoObrigatorio('a quantidade'))
      .test('quantidadeGtZero', 'A quantidade deve ser superior a 0', gtZero),
    valor: yup
      .number()
      .nullable()
      .required(mensagemCampoObrigatorio('o valor'))
      .test('valorGtZero', mensagemCampoObrigatorio('o valor'), gtZero),
  })
);

const produtosServicosVendaFuturaSchema = yup.array(
  yup.object().shape({
    produtoServico: yup
      .object()
      .nullable()
      .required(mensagemCampoObrigatorio('o produto')),
    quantidade: yup
      .number()
      .nullable()
      .required(mensagemCampoObrigatorio('a quantidade'))
      .test('quantidadeGtZero', 'A quantidade deve ser superior a 0', gtZero),
    valor: yup
      .number()
      .nullable()
      .required(mensagemCampoObrigatorio('o valor'))
      .test('valorGtZero', mensagemCampoObrigatorio('o valor'), gtZero),
  })
);

const produtosServicosVendaOrdemSchema = yup.array(
  yup.object().shape({
    produtoServico: yup
      .object()
      .nullable()
      .required(mensagemCampoObrigatorio('o produto')),
    quantidade: yup
      .number()
      .nullable()
      .required(mensagemCampoObrigatorio('a quantidade'))
      .test('quantidadeGtZero', 'A quantidade deve ser superior a 0', gtZero),
    valor: yup
      .number()
      .nullable()
      .required(mensagemCampoObrigatorio('o valor'))
      .test('valorGtZero', mensagemCampoObrigatorio('o valor'), gtZero),
  })
);

const pagamentoSchema = yup
  .object()
  .nullable()
  .shape({
    formaRecebimento: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('a forma de recebimento')),
    avista: yup
      .object()
      .nullable()
      .shape({
        dataVencimento: yup
          .object()
          .nullable()
          .test('dataVencimento', mensagemCampoObrigatorio('a data do vencimento'), function(dataVencimento) {
            return this.parent.tipoPagamento !== 'A_VISTA' || !!dataVencimento;
          }),
      }),
    parcelamento: yup
      .object()
      .nullable()
      .shape({
        quantidade: yup
          .number()
          .nullable()
          .test('quantidade', mensagemCampoObrigatorio('a quantidade'), function(quantidade) {
            return this.parent.tipoPagamento !== 'PARCELADO' || (quantidade !== undefined && quantidade !== null);
          })
          .test('minimo', 'A quantidade deve ser superior a 0', function(quantidade) {
            return this.parent.tipoPagamento !== 'PARCELADO' || quantidade > 0;
          })
          .test('maximo', 'A quantidade de parcelas deve ser de no máximo 72', function(quantidade) {
            return this.parent.tipoPagamento !== 'PARCELADO' || quantidade <= 72;
          }),
        diferencaParcelas: yup
          .number()
          .test('diferencaParcelas', 'A diferença deve ser R$0,00', function(diferencaParcelas) {
            return this.parent.tipoPagamento !== 'PARCELADO' || !diferencaParcelas;
          }),
        parcelas: yup.array(
          yup
            .object()
            .nullable()
            .shape({
              dataVencimento: yup
                .object()
                .nullable()
                .required(mensagemCampoObrigatorio('a data de vencimento')),
              valorParcela: yup
                .number()
                .nullable()
                .test('valorParcela', 'O valor deve ser maior que 0', function(valor) {
                  return (
                    this.parent.tipoPagamento !== 'PARCELADO' || (valor !== undefined && valor !== null && valor >= 0)
                  );
                })
                .required(mensagemCampoObrigatorio('o valor da parcela')),
            })
        ),
      }),
  });

const schemaVendaPadrao = yup.object().shape({
  data: yup
    .date()
    .nullable()
    .required(mensagemCampoObrigatorio('a data')),
  cliente: yup
    .object()
    .nullable()
    .test('cliente', mensagemCampoObrigatorio('o cliente'), function(cliente) {
      return !!cliente?.id;
    }),
  categorias: useCategoriasSchema(),
  diferencaCategorias: useDiferencaCategoriasSchema(),
  situacao: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('a situação')),
  motivoVenda: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('o motivo da venda')),
  valorDesconto: yup
    .number()
    .nullable()
    .test('valorDesconto', 'O valor deve ser superior a 0', function(valorDesconto) {
      const tipoDesconto = this.parent.tipoDesconto !== ' ' ? this.parent.tipoDesconto : false;

      return tipoDesconto ? gtZero(valorDesconto) : true;
    }),
  produtosServicos: produtosServicosVendaPadraoSchema,
  pagamento: pagamentoSchema,
  emailsCliente: yup.array().when('envioAutomaticoCobranca', {
    is: true,
    then: yup.array().of(
      yup.object().shape({
        email: yup
          .string()
          .email('E-mail inválido')
          .nullable()
          .test('email', mensagemCampoObrigatorio('o e-mail'), (email) => {
            return !!email;
          }),
      })
    ),
  }),
});

const schemaVendaFutura = yup.object().shape({
  data: yup
    .date()
    .nullable()
    .required(mensagemCampoObrigatorio('a data')),
  cliente: yup
    .object()
    .nullable()
    .test('cliente', mensagemCampoObrigatorio('o cliente'), function(cliente) {
      return !!cliente?.id;
    }),
  categorias: useCategoriasSchema(),
  diferencaCategorias: useDiferencaCategoriasSchema(),
  situacao: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('a situação')),
  motivoVenda: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('o motivo da venda')),
  valorDesconto: yup
    .number()
    .nullable()
    .test('valorDesconto', 'O valor deve ser superior a 0', function(valorDesconto) {
      const tipoDesconto = this.parent.tipoDesconto !== ' ' ? this.parent.tipoDesconto : false;

      return tipoDesconto ? gtZero(valorDesconto) : true;
    }),
  produtosServicos: produtosServicosVendaFuturaSchema,
  pagamento: pagamentoSchema,
  emailsCliente: yup.array().when('envioAutomaticoCobranca', {
    is: true,
    then: yup.array().of(
      yup.object().shape({
        email: yup
          .string()
          .email('E-mail inválido')
          .nullable()
          .test('email', mensagemCampoObrigatorio('o e-mail'), (email) => {
            return !!email;
          }),
      })
    ),
  }),
});

const schemaVendaOrdem = yup.object().shape({
  data: yup
    .date()
    .nullable()
    .required(mensagemCampoObrigatorio('a data')),
  cliente: yup
    .object()
    .nullable()
    .test('cliente', mensagemCampoObrigatorio('o cliente'), function(cliente) {
      return !!cliente?.id;
    }),
  categorias: useCategoriasSchema(),
  diferencaCategorias: useDiferencaCategoriasSchema(),
  situacao: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('a situação')),
  motivoVenda: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('o motivo da venda')),
  valorDesconto: yup
    .number()
    .nullable()
    .test('valorDesconto', 'O valor deve ser superior a 0', function(valorDesconto) {
      const tipoDesconto = this.parent.tipoDesconto !== ' ' ? this.parent.tipoDesconto : false;

      return tipoDesconto ? gtZero(valorDesconto) : true;
    }),
  produtosServicos: produtosServicosVendaOrdemSchema,
  pagamento: pagamentoSchema,
  emailsCliente: yup.array().when('envioAutomaticoCobranca', {
    is: true,
    then: yup.array().of(
      yup.object().shape({
        email: yup
          .string()
          .email('E-mail inválido')
          .nullable()
          .test('email', mensagemCampoObrigatorio('o e-mail'), (email) => {
            return !!email;
          }),
      })
    ),
  }),
});

const VENDA_SCHEMA = {
  VENDA_PADRAO: schemaVendaPadrao,
  VENDA_FUTURA: schemaVendaFutura,
  VENDA_ORDEM: schemaVendaOrdem,
};

export default VENDA_SCHEMA;
